import React, { useState } from 'react';
import { FaPlus, FaTrash } from 'react-icons/fa';
import { LANGUAGES } from '../constants';

const TipsEditor = ({ data, onUpdate, parameterName, activeLang, onLangChange }) => {
    const [subSection, setSubSection] = useState('tipsHeader'); // 'tipsHeader' | 'tipsSvg'
    // activeLang via props

    // Init data structure if missing
    const initSection = () => {
        onUpdate({
            tipsHeader: { en: "", hi: "", ar: "" },
            tipsSvg: [],
            tipsSvgText: { en: [], hi: [], ar: [] }
        });
    };

    if (!data || Object.keys(data).length === 0) {
        return (
            <div className="pcr-empty-state">
                <p>No Tips section found for this parameter.</p>
                <button className="pcr-btn-primary" onClick={initSection}>
                    <FaPlus /> Initialize Tips Section
                </button>
            </div>
        );
    }

    // Handlers
    const deleteTipsSection = () => {
        const confirmed = window.confirm(
            "Are you sure you want to delete the entire Health Tips section?\n\nThis will be permanent once you click Save Changes."
        );
        if (!confirmed) return;

        onUpdate({});
    };

    const updateHeader = (val) => {
        const newData = JSON.parse(JSON.stringify(data));
        if (!newData.tipsHeader) newData.tipsHeader = {};
        newData.tipsHeader[activeLang] = val;
        onUpdate(newData);
    };

    // Helper to find index from ID or Index
    // Helper to find index from ID or Index
    const getIndex = (list, idOrIdx) => {
        if (!list) return -1;
        if (typeof idOrIdx === 'number') {
            if (idOrIdx >= 0 && idOrIdx < list.length) return idOrIdx;
            return -1;
        }
        return list.findIndex(item => item && item.id === idOrIdx);
    };

    // TIPS SVG/LIST Handlers
    // Legacy mapping: 'tipsSvg' is array of strings (icon names)
    // 'tipsSvgText' is object { en: [], hi: [] } with matching arrays
    const addTipItem = () => {
        const newData = JSON.parse(JSON.stringify(data));
        // Ensure arrays exist
        if (!newData.tipsSvg) newData.tipsSvg = [];
        if (!newData.tipsSvgText) newData.tipsSvgText = {};

        // Use stable unique ID for new items
        // Switching 'tipsSvg' to store objects { id, icon } instead of just strings
        const newId = `tip_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        newData.tipsSvg.push({ id: newId, icon: "" });

        LANGUAGES.forEach(l => {
            if (!newData.tipsSvgText[l.code]) newData.tipsSvgText[l.code] = [];
            // If arrays act parallel, push to each lang array
            newData.tipsSvgText[l.code].push("");
        });
        onUpdate(newData);
    };

    const removeTipItem = (idOrIdx) => {
        const newData = JSON.parse(JSON.stringify(data));
        const index = getIndex(newData.tipsSvg, idOrIdx);

        if (index === -1) return;

        // Logic: Clear content for the active language ONLY (Smart Delete)
        // If the user wants to remove the ROW, they must clear all languages or use a different action.

        // 1. Clear text for active language
        if (!newData.tipsSvgText) newData.tipsSvgText = {};
        if (newData.tipsSvgText[activeLang]) {
            newData.tipsSvgText[activeLang][index] = "";
        }

        // 2. Check if text is empty for ALL languages for this index
        const allEmpty = LANGUAGES.every(l => {
            const text = newData.tipsSvgText[l.code]?.[index];
            return !text || text.trim() === "";
        });

        // 3. If all empty, THEN remove the row to clean up
        if (allEmpty) {
            if (newData.tipsSvg) newData.tipsSvg = newData.tipsSvg.filter((_, i) => i !== index);
            Object.keys(newData.tipsSvgText).forEach(key => {
                if (newData.tipsSvgText[key]) {
                    newData.tipsSvgText[key] = newData.tipsSvgText[key].filter((_, i) => i !== index);
                }
            });
        }

        onUpdate(newData);
    };

    const updateTipText = (idOrIdx, val) => {
        const newData = JSON.parse(JSON.stringify(data));
        const index = getIndex(newData.tipsSvg, idOrIdx);

        if (index === -1) return;

        if (!newData.tipsSvgText) newData.tipsSvgText = {};
        if (!newData.tipsSvgText[activeLang]) newData.tipsSvgText[activeLang] = [];
        // Ensure index exists
        while (newData.tipsSvgText[activeLang].length <= index) {
            newData.tipsSvgText[activeLang].push("");
        }
        newData.tipsSvgText[activeLang][index] = val;
        onUpdate(newData);
    };

    const updateTipIcon = (idOrIdx, val) => {
        const newData = JSON.parse(JSON.stringify(data));
        const index = getIndex(newData.tipsSvg, idOrIdx);

        if (index === -1) return;

        if (!newData.tipsSvg) newData.tipsSvg = [];

        // Handle both object (new) and string (legacy) structures
        if (typeof newData.tipsSvg[index] === 'object' && newData.tipsSvg[index] !== null) {
            newData.tipsSvg[index].icon = val;
        } else {
            newData.tipsSvg[index] = val;
        }
        onUpdate(newData);
    };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h2 className="pcr-section-title" style={{ margin: 0 }}>Health Tips</h2>
                <button
                    className="pcr-btn-danger-icon"
                    onClick={deleteTipsSection}
                    title="Delete entire Health Tips section"
                    style={{ padding: '0.4rem 0.6rem', height: 'auto', width: 'auto' }}
                >
                    <FaTrash />
                </button>
            </div>

            {/* Sub-Nav */}
            <div className="pcr-tabs-nav">
                <button
                    className={`pcr-tab-btn ${subSection === 'tipsHeader' ? 'active' : ''}`}
                    onClick={() => setSubSection('tipsHeader')}
                >
                    Header Title
                </button>
                <button
                    className={`pcr-tab-btn ${subSection === 'tipsSvg' ? 'active' : ''}`}
                    onClick={() => setSubSection('tipsSvg')}
                >
                    Tips List & Icons
                </button>
            </div>

            <div className="pcr-lang-tabs" style={{ marginTop: '1rem' }}>
                {LANGUAGES.map(lang => (
                    <button
                        type="button"
                        key={lang.code}
                        className={`pcr-lang-tab ${activeLang === lang.code ? 'active' : ''}`}
                        onClick={() => onLangChange(lang.code)}
                        aria-selected={activeLang === lang.code}
                    >
                        {lang.label}
                    </button>
                ))}
            </div>

            <div className="pcr-editor-body">
                {subSection === 'tipsHeader' && (
                    <textarea
                        className="pcr-textarea"
                        value={data.tipsHeader?.[activeLang] || ''}
                        onChange={(e) => updateHeader(e.target.value)}
                        placeholder="Enter tips header (e.g., 'Dietary Recommendations')..."
                    />
                )}

                {subSection === 'tipsSvg' && (
                    <div>
                        {data.tipsSvg && data.tipsSvg.map((item, idx) => {
                            // Determine if item is new object structure or legacy string
                            const isObject = typeof item === 'object' && item !== null;
                            const itemId = isObject ? item.id : idx;
                            const itemVal = isObject ? (item.icon || '') : item;

                            // Use ID as key if available (new items), otherwise idx (legacy)
                            // This ensures stability for new items as requested
                            const key = isObject ? item.id : idx;

                            return (
                                <div key={key} className="pcr-list-item-card">
                                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                                        <div style={{ flex: 1 }}>
                                            <label className="pcr-label-small">Tip Text ({activeLang})</label>
                                            <textarea
                                                className="pcr-textarea-small"
                                                value={data.tipsSvgText?.[activeLang]?.[idx] || ''}
                                                onChange={(e) => updateTipText(itemId, e.target.value)}
                                            />
                                        </div>
                                        <div style={{ width: '150px' }}>
                                            <label className="pcr-label-small">Icon/SVG Name</label>
                                            <input
                                                type="text"
                                                className="pcr-input-small"
                                                value={itemVal}
                                                onChange={(e) => updateTipIcon(itemId, e.target.value)}
                                            />
                                        </div>
                                        <button
                                            className="pcr-btn-danger-icon"
                                            onClick={() => removeTipItem(itemId)}
                                            style={{ marginTop: '1.5rem' }}
                                            aria-label="Delete tip"
                                        >
                                            <FaTrash />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}

                        <button className="pcr-btn-secondary" onClick={addTipItem}>
                            <FaPlus /> Add New Tip
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TipsEditor;
